<template>
  <div class="screenshare-booth">
    <header class="panel-header">
      <div class="panel-left-item">
        <button class="text-button narrow-button" @click="close">{{ __('Close') }}</button>
      </div>
      <div class="panel-title">{{ __('Screen recorder') }}</div>
      <div class="panel-right-item">
        <button v-show="hasRecordedChunks && !recording" class="text-button narrow-button" @click="done">
          {{ __('Playback and save') }}
        </button>
      </div>
    </header>
    <div class="panel-body">
      <!-- without muted attribute an echo is generated -->
      <video v-show="false" ref="player" :width="canvasWidth" :height="canvasHeight" autoplay muted />
      <div>
        <screenshare-instructions v-if="true" />
        <div v-if="canEnableVideoAndAudio" class="screenshare-booth-radio-button">
          <p class="small-caption-type form-label">Video</p>
          <div class="radio-button">
            <label
              ><input
                type="radio"
                :checked="videoSource == 'screen'"
                value="screen"
                @change="chooseScreenRecordingVideoSource"
              /><span>{{ __('Screen') }}</span></label
            >
            <label
              ><input
                type="radio"
                :checked="videoSource == 'window'"
                value="window"
                @change="chooseScreenRecordingVideoSource"
              /><span>{{ __('Application window') }}</span></label
            >
          </div>
        </div>

        <div v-if="isElectronApp" class="source-picker flex overflow-x-auto">
          <div
            v-for="source in electronAppSources"
            :key="source.id"
            :class="[
              'source-picker-item flex flex-col justify-between mx-1 p-1',
              source.id === streamId ? 'border-2 border-solid border-canary-500' : '',
            ]"
            @click="selectVideoSource(source.id)"
          >
            <div class="flex items-center justify-center h-full">
              <img :src="source.png" :alt="source.name" />
            </div>
            <div class="flex items-center">
              <img v-if="source.appIcon" :src="source.appIcon" :alt="source.name" class="app-icon mb-0.5" />
              <p class="caption-type text-sm truncate ml-1">
                {{ source.name }}
              </p>
            </div>
          </div>
        </div>

        <div v-if="canEnableVideoAndAudio" class="screenshare-booth-radio-button">
          <p class="small-caption-type form-label">Audio</p>
          <div class="radio-button">
            <label
              ><input type="radio" :checked="audioEnabled == true" value="true" @change="setAudioEnabled" /><span>
                {{ __('With audio') }}
              </span></label
            >
            <label
              ><input type="radio" :checked="audioEnabled == false" value="false" @change="setAudioEnabled" /><span>
                {{ __('Without audio') }}
              </span></label
            >
          </div>
        </div>
        <div v-if="isElectronApp" class="electron-audio-select-box mb-4">
          <screenshare-booth-audio-select
            v-if="audioEnabled == true"
            :audio-devices="audioDevices"
            :active-audio-device-id="activeAudioDeviceId"
            :choose-audio-device="chooseScreenRecordingAudioDevice"
          />
        </div>
        <div v-else-if="isChrome">
          <screenshare-booth-audio-select
            :audio-devices="audioDevices"
            :active-audio-device-id="activeAudioDeviceId"
            :choose-audio-device="chooseScreenRecordingAudioDevice"
          />
        </div>
      </div>
    </div>
    <footer v-show="!recording" class="panel-footer">
      <button
        v-show="!streaming && !hasReachedRecordingLimit && !hasRecordedChunks"
        :class="[
          'floating-action-button shadow-z2 hover-hover:hover:shadow-z3',
          isElectronApp && !streamId ? 'cursor-not-allowed' : '',
        ]"
        :title="recordingButtonTitle"
        :disabled="isElectronApp && !streamId"
        @click="requestVideoStream"
      >
        <base-icon class="rotate-icon-270" name="arrow_forward" />
      </button>
      <button v-show="hasRecordedChunks" class="text-button narrow-button" @click="restart">
        {{ __('Discard and restart') }}
      </button>
    </footer>
  </div>
</template>

<script>
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import InterstitialUploader from 'vuecomponents/interstitial_uploader'
import AVRecorderMixin from 'vuemixins/av_recorder_mixin'
import BaseIcon from 'vuecomponents/base_icon'
import OzLoadingSpinner from 'library/v4/components/OzLoadingSpinner.vue'
import CheckBox from 'vuecomponents/check_box'
import ScreenshareInstructions from 'vuecomponents/screenshare_instructions'
import ScreenshareBoothAudioSelect from 'vuecomponents/screenshare_booth_audio_select'
import { trackEvent } from 'bits/analytics'
import fetchElectronBridge from 'bits/electron_bridge'
import { captureException } from 'bits/error_tracker'
import { mapState } from 'vuex'

export default {
  components: {
    BaseIcon,
    OzLoadingSpinner,
    CheckBox,
    InterstitialUploader,
    ScreenshareInstructions,
    ScreenshareBoothAudioSelect,
  },
  mixins: [AVRecorderMixin],
  props: {
    width: Number,
    height: Number,
  },
  data() {
    return {
      maxDuration: 300, // in seconds
      bufferDuration: 15,
      canvasWidth: 640,
      canvasHeight: 360,
      title: '',
      streamId: null,
      audioEnabled: true,
      videoSource: 'screen',
      isClosingDisabled: false,
      desktopScreenAndWindowSources: [],
    }
  },
  computed: {
    ...mapState(['isChrome', 'isFirefox', 'isElectronApp']),
    constraints() {
      if (this.isElectronApp) {
        return {
          audio: this.audioEnabled,
          video: {
            mandatory: {
              chromeMediaSource: 'desktop',
              chromeMediaSourceId: this.streamId,
            },
          },
        }
      }
      if (this.isChrome) {
        return {
          audio: !this.audioEnabled || this.audioDevices.length === 0 ? false : 'chrome',
          video: {
            mandatory: {
              chromeMediaSource: 'desktop',
              chromeMediaSourceId: this.streamId,
            },
          },
        }
      }
      if (this.isFirefox) {
        return {
          audio: this.audioEnabled,
          video: {
            mediaSource: this.videoSource,
          },
        }
      }
      return {}
    },
    isReachingRecordingLimit() {
      return this.secondsElapsed === this.maxDuration - 30
    },
    electronAppSources() {
      const screenSources = this.desktopScreenAndWindowSources.filter((screen) => screen.id.includes('screen'))
      const windowSources = this.desktopScreenAndWindowSources.filter((screen) => screen.id.includes('window'))

      return this.videoSource === 'screen' ? screenSources : windowSources
    },
    canEnableVideoAndAudio() {
      return !this.isChrome || this.isElectronApp
    },
  },
  watch: {
    recordingDurationText(newValue) {
      this.$emit('recording-duration', newValue)
    },
    isReachingRecordingLimit(newValue) {
      if (newValue === true) {
        alert(this.__("You're running out of time."))
      }
    },
    recording(newValue) {
      if (!newValue) {
        if (this.isFirefox) {
          this.isClosingDisabled = false
        }
        this.$emit('recording-interrupted')
      }
    },
  },
  mounted() {
    // set values given by the parent
    this.canvasWidth = this.width
    this.canvasHeight = this.height
    if (this.isChrome || this.isElectronApp) {
      this.setupAudioStreaming()
    }
    if (this.isElectronApp) {
      this.getElectronDesktopScreenAndWindowSources()
    }
  },
  beforeDestroy() {
    this.stopStreaming()
  },
  methods: {
    close() {
      if (!this.isClosingDisabled) {
        this.$emit('close')
      }
    },
    async done() {
      /**
       * When you are done, there are two possibilities:
       * 1. We already have recording data, in which case, just emit the event.
       * 2. We don't have data and we need to wait for recording data to be available
       *    before we fire the done event.
       *    (When recording data is available, gotRecordingData is automatically called.)
       */
      trackEvent('Screenshare booth', 'Recorded', null, this.secondsElapsed)
      if (this.recordingStatus == 'stopped') {
        const blob = await this.recordedBlob()
        this.$emit('done', blob)
      } else {
        this.stopRecording()
      }
    },
    // callback from AVRecorderMixin
    async gotRecordedData() {
      const blob = await this.recordedBlob()
      this.$emit('done', blob)
    },
    // callback from AVRecorderMixin
    gotStream(stream) {
      const videoTrack = stream.getVideoTracks()[0]
      // not supported properly by many browsers, e.g. Safari
      if (typeof videoTrack.getSettings === 'function') {
        const settings = videoTrack.getSettings()
        if (settings.height) {
          this.hiddenCanvasHeight = videoTrack.getSettings().height
        }
        if (settings.width) {
          this.hiddenCanvasWidth = videoTrack.getSettings().width
        }
        if (settings.aspectRatio) {
          this.canvasHeight = Math.floor(this.canvasWidth / settings.aspectRatio)
        }
      }
      this.streaming = true
      this.$refs.player.srcObject = stream
      this.startRecording()
      this.$emit('record', this.audioEnabled)
    },
    // callback from AVRecorderMixin
    errorGettingStream(error) {
      if (this.isFirefox) {
        this.isClosingDisabled = false
      }
    },
    requestVideoStreamChrome() {
      window.postMessage({ type: 'SS_UI_REQUEST', text: 'start' }, '*')

      // listen for messages from the content-script
      window.addEventListener('message', (event) => {
        // NOTE: you should discard foreign events
        if (event.origin !== window.location.origin) {
          console.warn('ScreenStream: you should discard foreign event from origin:', origin)
          return
        }

        const {
          data: { type, streamId },
        } = event

        // content-script will send a 'SS_PING' msg if extension is installed
        if (type === 'SS_PING') {
        }

        // user chose a stream
        if (type === 'SS_DIALOG_SUCCESS') {
          if (!this.streaming) {
            this.streamId = streamId
            this.streaming = true
            this.mountRecording('video')
            this.mountScreenStreaming()
          }
        }

        // user clicked on 'cancel' in choose media dialog
        if (type === 'SS_DIALOG_CANCEL') {
        }
      })
    },
    requestVideoStreamDesktopApp() {
      if (chrome) {
        chrome.tabs.query({ currentWindow: true, active: true }, (tabs) => {
          const sources = ['screen', 'window', 'audio']
          const tab = tabs[0]
          chrome.desktopCapture.chooseDesktopMedia(sources, tab, (streamId) => {
            if (streamId) {
              this.streamId = streamId
              this.mountRecording('video')
              this.mountScreenStreaming()
            } else {
              this.close()
            }
          })
        })
      }
    },
    requestVideoStreamElectronApp() {
      const electronApp = fetchElectronBridge()
      if (!electronApp.captureWindow) return

      electronApp.captureWindow().then(() => {
        this.mountRecording('video')
        this.mountScreenStreaming()
      })
    },
    requestVideoStream() {
      if (this.isFirefox) {
        this.isClosingDisabled = true
      }
      this.stopStreaming()

      if (this.isElectronApp) {
        this.requestVideoStreamElectronApp()
      } else if (this.isChrome && !chrome.desktopCapture) {
        this.requestVideoStreamChrome()
      } else if (this.isChrome && chrome.desktopCapture) {
        this.requestVideoStreamDesktopApp()
      } else {
        this.mountRecording('video')
        this.mountScreenStreaming()
      }
    },
    setupAudioStreaming() {
      this.mountToSetupAudioStreaming().then(() => {
        // request microphone and re-get audio devices
        if (this.isElectronApp && this.audioDevices.length === 0) {
          const electronApp = fetchElectronBridge()
          electronApp.requestMic?.()
          electronApp.on?.('ELECTRON_REQUEST_MIC_DONE', () => {
            this.mountToSetupAudioStreaming()
          })
        }
      })
    },
    chooseScreenRecordingAudioDevice(e) {
      if (e.target.value === 'no_audio') {
        this.audioEnabled = false
      } else {
        this.audioEnabled = true
      }
      this.chooseAudioDevice(e)
    },
    chooseScreenRecordingVideoSource(e) {
      this.videoSource = e.target.value
    },
    setAudioEnabled(e) {
      this.audioEnabled = e.target.value == 'true'
    },
    restart() {
      this.discardRecording()
      this.requestVideoStream()
    },
    async getElectronDesktopScreenAndWindowSources() {
      const electronApp = fetchElectronBridge()
      if (!electronApp.captureWindow) return
      try {
        const sources = await electronApp.captureWindow()
        this.desktopScreenAndWindowSources = sources
      } catch (error) {
        captureException(error)
      }
    },
    selectVideoSource(sourceId) {
      this.streamId = sourceId
    },
  },
}
</script>

<style lang="scss" scoped>
@import './../../assets/stylesheets/3/modules/all';
.screenshare-booth {
  background-image: url('../images/mountain_graphic.svg');
  background-position: bottom 48px center;
  background-repeat: no-repeat;
  background-size: 100% auto;
  background-color: $grey-1;
  header {
    background-color: $white-color;
  }
  footer {
    position: relative;
    background-color: $white-color;
  }
}
.floating-action-button {
  position: absolute;
  top: -28px;
  left: calc(50% - 28px);
}
video {
  display: block;
}
.panel-body {
  position: relative;
  min-height: 360px;
}
.screenshare-booth-meta {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}
.screenshare-booth-meta-overlay {
  width: 100%;
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.54);
}
.screenshare-booth-radio-button {
  display: flex;
  flex-direction: column;
  margin: 0 40px;
}
.screenshare-booth-radio-button + .screenshare-booth-radio-button {
  padding-top: 12px;
}

.audio-enabled-mark-check {
  display: flex;
  align-items: center;
  height: 32px;
  padding: 0px 40px;
  span:last-child {
    margin: 0px 8px;
    font-size: 16px;
  }
}
.screenshare-booth-duration {
  position: absolute;
  left: 16px;
  bottom: 8px;
}
.screenshare-booth-beacon {
  position: absolute;
  bottom: 8px;
  right: 16px;
  display: flex;
  align-items: center;
  i {
    //the symbol is not aligned.
    margin-top: -2px;
    margin-left: 4px;
    color: $red-color;
  }
}
.screenshare-booth-beacon-text {
  line-height: 24px;
  font-size: 13px;
  text-transform: uppercase;
}
.screenshare-booth-runningout {
  font-size: 14px;
  padding: 0px 8px;
}

.select-box i {
  position: absolute;
  top: 7px;
  left: 4px;
  line-height: 18px;
  color: var(--muted-text-color);
}

.source-picker {
  height: 140px;
  margin: 12px 40px 0;

  .source-picker-item {
    min-width: 154px;

    img {
      max-height: 84px;
    }

    .app-icon {
      width: 16px;
    }
  }
}

.electron-audio-select-box {
  min-height: 64px;
}
</style>
